/**
 * AdminPreferenceShareTemplatesPage - Slice G
 *
 * Admin-managed share-page templates: per category or per occasion, define a
 * hero, headline, sub-copy, CTA, and an optional waitlist popup that captures
 * emails into a Beehiiv field.
 */
import { useEffect, useMemo, useState } from "react";
import { Loader2, Plus, Save, Trash2, Upload, ArrowUp, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("session_token")}` });

const CTA_ACTIONS = [
  { key: "intake",         label: "Open intake form" },
  { key: "open_waitlist",  label: "Open waitlist popup" },
  { key: "external",       label: "Go to external URL" },
];

export default function AdminPreferenceShareTemplatesPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState("category");
  const [scopeId, setScopeId] = useState("");
  const [categories, setCategories] = useState([]);
  const [occasions, setOccasions] = useState([]);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r, rc, ro] = await Promise.all([
        fetch(`${API}/admin/preference-share-templates`, { headers: auth() }),
        fetch(`${API}/admin/categories`, { headers: auth() }),
        fetch(`${API}/admin/master-occasions`, { headers: auth() }),
      ]);
      if (r.ok) {
        const d = await r.json();
        setRows(d.templates || []);
      }
      if (rc.ok) {
        const dc = await rc.json();
        setCategories(dc.categories || dc || []);
      }
      if (ro.ok) {
        const dro = await ro.json();
        setOccasions(dro.occasions || []);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const scopeOptions = useMemo(() => {
    if (scope === "occasion") return occasions.map(o => ({ id: o.occasion_id, label: o.name }));
    return (categories || []).map(c => ({ id: c.category_id, label: c.label || c.name || c.category_id }));
  }, [scope, categories, occasions]);

  const create = async () => {
    if (!scopeId) { toast.error("Pick a scope_id first"); return; }
    setSaving(true);
    try {
      const body = { scope, scope_id: scopeId, name: scopeOptions.find(o => o.id === scopeId)?.label || `${scope} · ${scopeId}` };
      const r = await fetch(`${API}/admin/preference-share-templates`, {
        method: "POST", headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (r.ok) {
        const d = await r.json();
        toast.success("Template created");
        setEditing(d.template_id);
        setDraft(d.template);
        setScopeId("");
        load();
      } else toast.error("Create failed");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (t) => { setEditing(t.template_id); setDraft({ ...t }); };
  const cancelEdit = () => { setEditing(null); setDraft(null); };

  const saveEdit = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const r = await fetch(`${API}/admin/preference-share-templates/${editing}`, {
        method: "PUT", headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify(draft),
      });
      if (r.ok) { toast.success("Template saved"); load(); cancelEdit(); }
      else toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete share template "${t.name}"?`)) return;
    const r = await fetch(`${API}/admin/preference-share-templates/${t.template_id}`, {
      method: "DELETE", headers: auth(),
    });
    if (r.ok) { toast.success("Deleted"); load(); }
    else toast.error("Delete failed");
  };

  const uploadHero = async (file) => {
    if (!file || !draft) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("folder", "preference_share_templates");
    const r = await fetch(`${API}/upload/cloudinary`, { method: "POST", headers: auth(), body: fd });
    if (r.ok) {
      const d = await r.json();
      setDraft(prev => ({ ...prev, hero_image_url: d.url || d.secure_url }));
      toast.success("Hero uploaded");
    } else toast.error("Upload failed");
  };

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto" data-testid="admin-pref-share-templates-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2"><ArrowUp className="w-5 h-5" /> Share pages</h1>
          <p className="text-xs text-stone-500">Per category / occasion: hero image, copy, CTA, optional waitlist popup → Beehiiv.</p>
        </div>
      </div>

      {/* Create row */}
      <div className="bg-white border border-stone-200 rounded-xl p-3 mb-4 flex items-end gap-2 flex-wrap" data-testid="pst-create-row">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-stone-500 block mb-0.5">Scope</label>
          <Select value={scope} onValueChange={(v) => { setScope(v); setScopeId(""); }}>
            <SelectTrigger className="w-32 h-8 text-xs" data-testid="pst-scope">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="category">Category</SelectItem>
              <SelectItem value="occasion">Occasion</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="text-[10px] uppercase tracking-wider text-stone-500 block mb-0.5">{scope === "occasion" ? "Occasion" : "Category"}</label>
          <Select value={scopeId} onValueChange={setScopeId}>
            <SelectTrigger className="h-8 text-xs" data-testid="pst-scope-id">
              <SelectValue placeholder={`Pick ${scope}`} />
            </SelectTrigger>
            <SelectContent>
              {scopeOptions.map(o => (
                <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button size="sm" onClick={create} disabled={saving || !scopeId} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="pst-create-btn">
          {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Plus className="w-3 h-3 mr-1" />}New template
        </Button>
      </div>

      {loading && <Loader2 className="w-5 h-5 animate-spin text-stone-400 mx-auto mt-8" />}
      {!loading && rows.length === 0 && (
        <p className="text-center text-sm text-stone-400 py-8">No share-page templates yet. Pick a scope above and create one.</p>
      )}

      <div className="space-y-3">
        {rows.map((t) => {
          const isEditing = editing === t.template_id;
          const d = isEditing ? draft : t;
          return (
            <div key={t.template_id} className="bg-white border border-stone-200 rounded-xl overflow-hidden" data-testid={`pst-row-${t.template_id}`}>
              <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-stone-100">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-stone-100 text-stone-600">{t.scope}</span>
                    <h3 className="text-sm font-semibold truncate">{t.name}</h3>
                    {!t.active && <span className="text-[10px] text-stone-400">(inactive)</span>}
                  </div>
                  <p className="text-[11px] text-stone-400">{t.scope}: {t.scope_id}</p>
                </div>
                <div className="flex items-center gap-1">
                  {!isEditing && <Button size="sm" variant="outline" onClick={() => startEdit(t)} data-testid={`pst-edit-${t.template_id}`}>Edit</Button>}
                  {isEditing && (
                    <>
                      <Button size="sm" onClick={saveEdit} disabled={saving} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid={`pst-save-${t.template_id}`}>
                        {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}Save
                      </Button>
                      <Button size="sm" variant="ghost" onClick={cancelEdit}>Cancel</Button>
                    </>
                  )}
                  <Button size="sm" variant="ghost" className="text-stone-400 hover:text-red-500" onClick={() => remove(t)} data-testid={`pst-del-${t.template_id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </div>

              {isEditing && d && (
                <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid={`pst-editor-${t.template_id}`}>
                  <div className="md:col-span-2">
                    <label className="text-xs text-stone-500 block mb-1">Hero image</label>
                    <div className="flex items-center gap-2">
                      {d.hero_image_url && <img src={d.hero_image_url} alt="" className="w-16 h-16 rounded object-cover border" />}
                      <input type="file" accept="image/*" onChange={(e) => e.target.files?.[0] && uploadHero(e.target.files[0])} className="text-xs" data-testid={`pst-hero-upload-${t.template_id}`} />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-stone-500 block mb-0.5">Display name</label>
                    <Input className="h-8 text-xs" value={d.name || ""} onChange={(e) => setDraft(p => ({ ...p, name: e.target.value }))} data-testid={`pst-name-${t.template_id}`} />
                  </div>
                  <div>
                    <label className="text-xs text-stone-500 block mb-0.5">Headline</label>
                    <Input className="h-8 text-xs" value={d.headline || ""} onChange={(e) => setDraft(p => ({ ...p, headline: e.target.value }))} placeholder="e.g. Save your makeup preferences" data-testid={`pst-headline-${t.template_id}`} />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-xs text-stone-500 block mb-0.5">Sub-copy</label>
                    <Textarea rows={2} value={d.sub_copy || ""} onChange={(e) => setDraft(p => ({ ...p, sub_copy: e.target.value }))} className="text-xs" data-testid={`pst-subcopy-${t.template_id}`} />
                  </div>
                  <div>
                    <label className="text-xs text-stone-500 block mb-0.5">CTA label</label>
                    <Input className="h-8 text-xs" value={d.cta_label || ""} onChange={(e) => setDraft(p => ({ ...p, cta_label: e.target.value }))} data-testid={`pst-cta-label-${t.template_id}`} />
                  </div>
                  <div>
                    <label className="text-xs text-stone-500 block mb-0.5">CTA action</label>
                    <Select value={d.cta_action || "intake"} onValueChange={(v) => setDraft(p => ({ ...p, cta_action: v }))}>
                      <SelectTrigger className="h-8 text-xs" data-testid={`pst-cta-action-${t.template_id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CTA_ACTIONS.map(a => <SelectItem key={a.key} value={a.key}>{a.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {d.cta_action === "external" && (
                    <div className="md:col-span-2">
                      <label className="text-xs text-stone-500 block mb-0.5">External URL</label>
                      <Input className="h-8 text-xs" value={d.cta_url || ""} onChange={(e) => setDraft(p => ({ ...p, cta_url: e.target.value }))} placeholder="https://..." data-testid={`pst-cta-url-${t.template_id}`} />
                    </div>
                  )}

                  {/* Waitlist popup */}
                  <div className="md:col-span-2 border-t border-stone-100 pt-3 mt-1" data-testid={`pst-waitlist-${t.template_id}`}>
                    <label className="flex items-center gap-2 text-xs text-stone-700 mb-2">
                      <input type="checkbox" checked={!!d.waitlist_popup?.enabled} onChange={(e) => setDraft(p => ({ ...p, waitlist_popup: { ...(p.waitlist_popup || {}), enabled: e.target.checked } }))} data-testid={`pst-waitlist-toggle-${t.template_id}`} />
                      <Sparkles className="w-3.5 h-3.5 text-purple-500" />
                      Show a waitlist popup (Beehiiv)
                    </label>
                    {d.waitlist_popup?.enabled && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 ml-5">
                        <div className="md:col-span-2">
                          <label className="text-[10px] text-stone-500 block mb-0.5">Popup copy</label>
                          <Textarea rows={2} value={d.waitlist_popup?.copy || ""} onChange={(e) => setDraft(p => ({ ...p, waitlist_popup: { ...(p.waitlist_popup || {}), copy: e.target.value } }))} className="text-xs" data-testid={`pst-waitlist-copy-${t.template_id}`} />
                        </div>
                        <div>
                          <label className="text-[10px] text-stone-500 block mb-0.5">Beehiiv field</label>
                          <Input className="h-8 text-xs" value={d.waitlist_popup?.beehiiv_field || ""} onChange={(e) => setDraft(p => ({ ...p, waitlist_popup: { ...(p.waitlist_popup || {}), beehiiv_field: e.target.value } }))} placeholder="e.g. waitlist_makeup" data-testid={`pst-waitlist-beehiiv-${t.template_id}`} />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="md:col-span-2 flex items-center gap-2 pt-2 border-t border-stone-100 mt-1">
                    <label className="flex items-center gap-2 text-xs text-stone-700">
                      <input type="checkbox" checked={d.active !== false} onChange={(e) => setDraft(p => ({ ...p, active: e.target.checked }))} data-testid={`pst-active-${t.template_id}`} />
                      Active (visible on public share pages)
                    </label>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
